import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import { toast } from "react-toastify";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import {
  getEducatorPolicyTemplate,
  getSignedEducatorPolicies,
  uploadEducatorPolicyTemplate,
  uploadWorkAgreementTemplate,
} from "services/educatorPolicyService";

function EducatorPolicy() {
  const [template, setTemplate] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [file, setFile] = useState(null);
  const [agreementFile, setAgreementFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingAgreement, setUploadingAgreement] = useState(false);

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const [templateResponse, signatureResponse] = await Promise.all([
        getEducatorPolicyTemplate(),
        getSignedEducatorPolicies(),
      ]);
      setTemplate(templateResponse.data || null);
      setSignatures(signatureResponse.data || []);
    } catch (error) {
      toast.error(error?.message || "Could not load the educator policy");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplate();
  }, []);

  const acceptPdf = (event, setNextFile) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile) return;
    const isPdf = nextFile.type === "application/pdf" || nextFile.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Choose a PDF file");
      event.target.value = "";
      return;
    }
    if (nextFile.size > 10 * 1024 * 1024) {
      toast.error("PDF must be smaller than 10MB");
      event.target.value = "";
      return;
    }
    setNextFile(nextFile);
  };

  const handleFile = (event) => acceptPdf(event, setFile);
  const handleAgreementFile = (event) => acceptPdf(event, setAgreementFile);

  const handleUpload = async () => {
    if (!file) {
      toast.error("Choose the policy PDF first");
      return;
    }
    setUploading(true);
    try {
      const response = await uploadEducatorPolicyTemplate(file);
      setTemplate(response.data || null);
      setFile(null);
      toast.success(response.message || "Educator policy uploaded");
    } catch (error) {
      toast.error(error?.message || "Could not upload the educator policy");
    } finally {
      setUploading(false);
    }
  };

  const handleAgreementUpload = async () => {
    if (!agreementFile) {
      toast.error("Choose the work agreement PDF first");
      return;
    }
    setUploadingAgreement(true);
    try {
      const response = await uploadWorkAgreementTemplate(agreementFile);
      setTemplate(response.data || null);
      setAgreementFile(null);
      toast.success(response.message || "Work agreement uploaded");
    } catch (error) {
      toast.error(error?.message || "Could not upload the work agreement");
    } finally {
      setUploadingAgreement(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonTypography variant="h5" fontWeight="medium" mb={1}>
              Educator Policy
            </ArgonTypography>
            {/* <ArgonTypography variant="button" color="text" mb={3} display="block">
              Upload the policy PDF here. On the tutor app, after documents are verified, this PDF
              opens with the tutor&apos;s name, email, Aadhaar number and today&apos;s date filled
              in. The tutor signs on the screen and the signed PDF is saved on their profile.
              Upload a new PDF whenever the policy changes. Tutors who already signed will be asked
              to sign the new version. Leave out any one tutor&apos;s name from the file. The app
              adds that on the last page.
            </ArgonTypography> */}

            {loading ? (
              <ArgonTypography variant="button">Loading current policy...</ArgonTypography>
            ) : template?.url ? (
              <ArgonBox mb={3}>
                <ArgonTypography variant="button" fontWeight="medium" display="block">
                  Current file: {template.fileName || "Educator policy"} (version {template.version})
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text" display="block" mb={1}>
                  Uploaded {template.uploadedAt ? new Date(template.uploadedAt).toLocaleString() : ""}
                </ArgonTypography>
                <ArgonButton
                  component="a"
                  href={template.url}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  color="info"
                  size="small"
                >
                  View PDF
                </ArgonButton>
              </ArgonBox>
            ) : (
              <ArgonTypography variant="button" display="block" mb={3}>
                No policy PDF uploaded yet.
              </ArgonTypography>
            )}

            <ArgonBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <ArgonButton component="label" variant="outlined" color="primary" size="small">
                {file ? file.name : "Choose PDF"}
                <input hidden type="file" accept="application/pdf,.pdf" onChange={handleFile} />
              </ArgonButton>
              <ArgonButton
                variant="gradient"
                color="info"
                size="small"
                onClick={handleUpload}
                disabled={uploading || !file}
              >
                {uploading ? "Uploading..." : "Upload policy"}
              </ArgonButton>
            </ArgonBox>

            <ArgonBox mt={4} pt={3} sx={{ borderTop: "1px solid #e9ecef" }}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={1}>
                Work Agreement
              </ArgonTypography>
              {/* <ArgonTypography variant="button" color="text" mb={2} display="block">
                Upload the work agreement PDF here. The tutor app shows this PDF under the educator
                policy. Name, father name, address, Aadhaar, contact and date are filled from the
                tutor profile. One signature is saved on both PDFs.
              </ArgonTypography> */}
              {template?.workAgreementUrl ? (
                <ArgonBox mb={2}>
                  <ArgonTypography variant="button" fontWeight="medium" display="block">
                    Current file: {template.workAgreementFileName || "Work agreement"} (version {template.workAgreementVersion})
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text" display="block" mb={1}>
                    Uploaded {template.workAgreementUploadedAt ? new Date(template.workAgreementUploadedAt).toLocaleString() : ""}
                  </ArgonTypography>
                  <ArgonButton
                    component="a"
                    href={template.workAgreementUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    color="info"
                    size="small"
                  >
                    View PDF
                  </ArgonButton>
                </ArgonBox>
              ) : (
                <ArgonTypography variant="button" display="block" mb={2}>
                  No work agreement PDF uploaded yet.
                </ArgonTypography>
              )}
              <ArgonBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <ArgonButton component="label" variant="outlined" color="primary" size="small">
                  {agreementFile ? agreementFile.name : "Choose PDF"}
                  <input hidden type="file" accept="application/pdf,.pdf" onChange={handleAgreementFile} />
                </ArgonButton>
                <ArgonButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={handleAgreementUpload}
                  disabled={uploadingAgreement || !agreementFile}
                >
                  {uploadingAgreement ? "Uploading..." : "Upload work agreement"}
                </ArgonButton>
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>
        </Card>

        <Card sx={{ mt: 3 }}>
          <ArgonBox p={3}>
            <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
              Signed by tutors
            </ArgonTypography>
            {loading ? (
              <ArgonTypography variant="button">Loading signed policies...</ArgonTypography>
            ) : signatures.length === 0 ? (
              <ArgonTypography variant="button" color="text">
                No tutor has signed the policy yet.
              </ArgonTypography>
            ) : (
              <Table
                columns={[
                  { name: "tutor", align: "left" },
                  { name: "phone", align: "left" },
                  { name: "signed on", align: "left" },
                  { name: "version", align: "left" },
                  { name: "pdf", align: "center" },
                  { name: "agreement", align: "center" },
                ]}
                rows={signatures.map((item) => ({
                  tutor: (
                    <ArgonTypography
                      component={Link}
                      to={`/tutors/profile/${item.id}`}
                      variant="caption"
                      color="info"
                      fontWeight="medium"
                    >
                      {item.fullName || "Tutor"}
                    </ArgonTypography>
                  ),
                  phone: (
                    <ArgonTypography variant="caption" color="text">
                      {item.phone || "—"}
                    </ArgonTypography>
                  ),
                  "signed on": (
                    <ArgonTypography variant="caption" color="text">
                      {item.signedAt ? new Date(item.signedAt).toLocaleString() : "—"}
                    </ArgonTypography>
                  ),
                  version: (
                    <ArgonTypography variant="caption" color={item.isCurrent ? "success" : "warning"} fontWeight="medium">
                      {item.isCurrent ? `Current (v${item.templateVersion})` : `Old v${item.templateVersion}`}
                    </ArgonTypography>
                  ),
                  pdf: (
                    <ArgonTypography
                      component="a"
                      href={item.signedPdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      variant="caption"
                      color="info"
                      fontWeight="medium"
                    >
                      View PDF
                    </ArgonTypography>
                  ),
                  agreement: item.signedWorkAgreementUrl ? (
                    <ArgonTypography
                      component="a"
                      href={item.signedWorkAgreementUrl}
                      target="_blank"
                      rel="noreferrer"
                      variant="caption"
                      color="info"
                      fontWeight="medium"
                    >
                      View PDF
                    </ArgonTypography>
                  ) : (
                    <ArgonTypography variant="caption" color="text">—</ArgonTypography>
                  ),
                }))}
              />
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EducatorPolicy;
